// Outils de développement (exposés sur window.dev en mode dev uniquement) : cadrer, simuler le temps.
import * as THREE from 'three';
import type { App } from './App';
import type { Character } from './Character';
import { POSE_TUNING } from '../presence/Presence';
import { HumanImpl } from '../human';
import { drapeTuning, lastDrapeStats } from '../human/drape';
import { pickBind } from '../presence/SoftBody';

export function installDevtools(app: App, getHer: () => Character | undefined) {
  const ready = () => new Promise<void>((r) => {
    const c = () => (getHer() && document.getElementById('loader')!.classList.contains('done') ? r() : setTimeout(c, 150));
    c();
  });
  /** Avance la simulation de `seconds` sans attendre l'horloge (utile quand l'onglet est masqué). */
  const settle = (seconds = 1) => {
    const n = Math.round(seconds * 60);
    for (let i = 0; i < n; i++) {
      app.controls.update();
      app.director.update(1 / 60);
      for (const u of app.updaters) u(1 / 60, 0);
      app.stage.update(1 / 60);
    }
  };
  /** Place la caméra : 'face' | 'upper' | 'body' | 'full', azimut en degrés (0 = face). */
  const shot = async (framing: 'face' | 'upper' | 'body' | 'full' = 'face', azimuthDeg = 18, opts: { mood?: string; freeze?: boolean } = {}) => {
    await ready();
    const her = getHer()!;
    const head = her.human.bones.get('head')!.getWorldPosition(new THREE.Vector3());
    const conf = { face: [0.07, 0.75, 0.04], upper: [-0.2, 1.95, 0.05], body: [-0.6, 3.6, 0.1], full: [-0.75, 4.6, 0.12] }[framing];
    const target = new THREE.Vector3(0, head.y + conf[0], 0);
    const az = THREE.MathUtils.degToRad(azimuthDeg);
    app.director.cancel();
    app.controls.target.copy(target);
    app.camera.position.set(Math.sin(az) * conf[1], target.y + conf[1] * conf[2], Math.cos(az) * conf[1]);
    app.controls.update();
    if (opts.mood) her.presence.setMood(opts.mood);
    if (opts.freeze) {
      // Fige les comportements aléatoires (regards détournés, clignements) pour une capture propre.
      (her.presence as unknown as Record<string, number>).nextAside = 1e9;
      (her.presence as unknown as Record<string, number>).nextBlink = 1e9;
      (her.behavior as unknown as Record<string, number>).moodUntil = 1e9;
    }
    settle(1.6);
    return 'ok';
  };
  /**
   * Planche d'expressions : rend chaque jeu de poids (morphs ARKit et unités osseuses "bu:Nom") sur un gros plan
   * figé et affiche le tout en une image (labo pour régler sourires et rires).
   */
  const lab = async (sets: Array<[string, Record<string, number>]>, opts: { dy?: number; dist?: number; az?: number; cols?: number; zoom?: number } = {}) => {
    await ready();
    const her = getHer()!, h = her.human, rig = her.presence.rig, fu = her.presence.faceUnits;
    app.updaters.length = 0;
    const e1 = h.bones.get('eye.L')!.getWorldPosition(new THREE.Vector3()), e2 = h.bones.get('eye.R')!.getWorldPosition(new THREE.Vector3());
    const mid = e1.add(e2).multiplyScalar(0.5);
    const ty = mid.y + (opts.dy ?? -0.045), dist = opts.dist ?? 0.42, az = THREE.MathUtils.degToRad(opts.az ?? 0);
    app.controls.target.set(0, ty, mid.z);
    app.camera.position.set(Math.sin(az) * dist, ty + 0.01, mid.z + Math.cos(az) * dist);
    app.camera.lookAt(app.controls.target);
    app.camera.updateMatrixWorld();
    const reset = () => { for (const n of h.morphNames) h.setMorph(n, 0); };
    const canvas = app.renderer.gl.domElement;
    const W = canvas.width, H = canvas.height, cw = 250, ch = 230, cols = opts.cols ?? 4;
    const out = document.createElement('canvas');
    out.width = cols * cw; out.height = Math.ceil(sets.length / cols) * (ch + 20);
    const g = out.getContext('2d')!;
    g.fillStyle = '#111'; g.fillRect(0, 0, out.width, out.height);
    const sw = Math.min(W, H) * (opts.zoom ?? 0.7), sh = sw * ch / cw, sx = (W - sw) / 2, sy = (H - sh) / 2;
    sets.forEach(([name, m], i) => {
      reset();
      const bw = new Map<string, number>();
      for (const k in m) { if (k.startsWith('bu:')) bw.set(k.slice(3), m[k]); else h.setMorph(k, m[k]); }
      rig.begin(); fu?.apply(rig, bw); rig.apply(); h.root.updateMatrixWorld(true);
      app.post.render(1 / 60); app.post.render(1 / 60);
      const x = (i % cols) * cw, y = Math.floor(i / cols) * (ch + 20);
      g.drawImage(canvas, sx, sy, sw, sh, x, y, cw, ch);
      g.fillStyle = '#fff'; g.font = '13px sans-serif'; g.fillText(name, x + 5, y + ch + 14);
    });
    reset();
    let img = document.getElementById('lab') as HTMLImageElement | null;
    if (!img) {
      img = document.createElement('img'); img.id = 'lab';
      img.style.cssText = 'position:fixed;inset:0;margin:auto;max-width:100vw;max-height:100vh;z-index:99;background:#111';
      document.body.appendChild(img);
    }
    img.src = out.toDataURL('image/jpeg', 0.9);
    await img.decode();
    return fu ? fu.names.length : 0;
  };
    /**
   * Matrice de test vêtements : pour chaque configuration (tenue, morphologie, pose), applique, laisse la pose
   * s'installer, cadre et rend une vignette ; assemble une planche (détection de transpercements).
   */
  const matrix = async (configs: Array<{ label: string; clothes: string[]; macro?: Record<string, number>; pose?: string; framing?: 'upper' | 'body' | 'full'; az?: number }>) => {
    await ready();
    const her = getHer()!, h = her.human, ed = her.editable;
    const base = JSON.parse(JSON.stringify(h.params));
    const canvas = app.renderer.gl.domElement;
    const cw = 200, ch = 260, cols = Math.min(4, configs.length);
    const out = document.createElement('canvas');
    out.width = cols * cw; out.height = Math.ceil(configs.length / cols) * (ch + 18);
    const g = out.getContext('2d')!;
    g.fillStyle = '#111'; g.fillRect(0, 0, out.width, out.height);
    let i = 0;
    for (const c of configs) {
      const cur = Object.keys(h.current.proxies).filter((k) => k.startsWith('clothes:'));
      for (const k of cur) if (!c.clothes.includes(k.slice(8))) await ed.setProxy(k as `clothes:${string}`, null);
      for (const id of c.clothes) if (!(('clothes:' + id) in h.current.proxies)) await ed.setProxy(`clothes:${id}`, id);
      const p = JSON.parse(JSON.stringify(base));
      Object.assign(p.macro, c.macro ?? {});
      await ed.setParams(p);
      await her.playPose(c.pose ?? 'idle/relaxed', 0);
      settle(0.6);
      await shot(c.framing ?? 'body', c.az ?? 18, { mood: 'sereine', freeze: true });
      settle(0.3);
      app.post.render(1 / 60);
      const sw = canvas.width * 0.62, sh = sw * ch / cw;
      const x = (i % cols) * cw, y = Math.floor(i / cols) * (ch + 18);
      g.drawImage(canvas, (canvas.width - sw) / 2, Math.max(0, (canvas.height - sh) / 2), sw, Math.min(sh, canvas.height), x, y, cw, ch);
      g.fillStyle = '#fff'; g.font = '11px sans-serif'; g.fillText(c.label, x + 4, y + ch + 13);
      i++;
    }
    await ed.setParams(base);
    let img = document.getElementById('lab') as HTMLImageElement | null;
    if (!img) {
      img = document.createElement('img'); img.id = 'lab';
      img.style.cssText = 'position:fixed;inset:0;margin:auto;max-width:100vw;max-height:100vh;z-index:99;background:#111';
      document.body.appendChild(img);
    }
    img.src = out.toDataURL('image/jpeg', 0.88);
    await img.decode();
    return configs.length;
  };
  /** Planche drapé / skinning seul, côte à côte, pour une liste de poses (vêtements portés actuellement). */
  const drapeBoard = async (poses: string[], opts: { framing?: 'upper' | 'body' | 'full'; az?: number } = {}) => {
    await ready();
    const her = getHer()!, h = her.human;
    her.autoPosture = false;
    const canvas = app.renderer.gl.domElement;
    const cw = 220, ch = 260, per = Math.min(3, poses.length);
    const out = document.createElement('canvas');
    out.width = cw * 2 * per; out.height = Math.ceil(poses.length / per) * (ch + 18);
    const g = out.getContext('2d')!;
    g.fillStyle = '#111'; g.fillRect(0, 0, out.width, out.height);
    const stats: Array<[string, number, number]> = [];
    for (let i = 0; i < poses.length; i++) {
      const p = poses[i], name = p.split('/').pop()!;
      her.invalidateDrape();
      await her.playPose(p, 0);
      settle(2.4);
      await shot(opts.framing ?? 'body', opts.az ?? 35, { mood: 'sereine', freeze: true });
      settle(0.4);
      stats.push([name, +lastDrapeStats.torn.toFixed(3), +lastDrapeStats.inside.toFixed(3)]);
      const grab = (col: number, label: string) => {
        app.post.render(1 / 60);
        const sw = canvas.width * 0.5, sh = sw * ch / cw;
        const x = ((i % per) * 2 + col) * cw, y = Math.floor(i / per) * (ch + 18);
        g.drawImage(canvas, (canvas.width - sw) / 2, Math.max(0, (canvas.height - sh) / 2), sw, Math.min(sh, canvas.height), x, y, cw, ch);
        g.fillStyle = '#fff'; g.font = '11px sans-serif'; g.fillText(label, x + 4, y + ch + 13);
      };
      grab(0, name.slice(0, 24) + (lastDrapeStats.rejected ? ' (rejeté)' : ' drapé'));
      h.undrape?.();
      for (let k = 0; k < 40; k++) h.updateDrape?.(1 / 60);
      grab(1, 'skinning');
    }
    let img = document.getElementById('lab') as HTMLImageElement | null;
    if (!img) {
      img = document.createElement('img'); img.id = 'lab';
      img.style.cssText = 'position:fixed;inset:0;margin:auto;max-width:100vw;max-height:100vh;z-index:99;background:#111';
      document.body.appendChild(img);
    }
    img.src = out.toDataURL('image/jpeg', 0.9);
    await img.decode();
    return stats;
  };
    (window as unknown as { dev: unknown }).dev = { drapeBoard, app, her: getHer, settle, shot, ready, pose: POSE_TUNING, lab, matrix, HumanImpl, drapeStats: lastDrapeStats, drapeTuning, pickBind };
}
